import { Component, computed, input, output, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { DropDownListComponent, KENDO_DROPDOWNLIST, KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_FORMFIELD } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { ClearableDropdownlistDirective } from '@unity/directives/clearable-dropdownlist.directive';
import { ControlWrapperComponent } from '@unity/forms/composed/control-wrapper/control-wrapper.component';
import { SortPipe } from '@unity/pipes/sort.pipe';
import { SortConfig } from '@unity/utils/types';

@Component({
  selector: 'unity-dropdown-control',
  standalone: true,
  imports: [
    KENDO_DROPDOWNS,
    KENDO_DROPDOWNLIST,
    KENDO_LOADER,
    KENDO_FORMFIELD,
    KENDO_LABEL,
    KENDO_BUTTONS,
    ReactiveFormsModule,
    SortPipe,
    ClearableDropdownlistDirective,
    ControlWrapperComponent,
  ],
  templateUrl: './dropdown-control.component.html',
  styleUrl: './dropdown-control.component.scss',
})
export class DropdownControlComponent<T extends Record<string, unknown>> {
  private readonly $dropdownlistReference = viewChild.required<DropDownListComponent>('dropdownlist');

  protected readonly $defaultSortCfg = computed<SortConfig[]>(() => [
    {
      field: this.$nameField(),
      dir: 'asc',
    },
  ]);

  public readonly $control = input.required<FormControl>({ alias: 'control' });
  public readonly $displayName = input.required<string>({ alias: 'displayName' });
  public readonly $description = input.required<string>({ alias: 'description' });
  public readonly $values = input.required<T[]>({ alias: 'values' });

  public readonly $showHint = input(true, { alias: 'showHint' });

  public readonly $nameField = input('name', { alias: 'nameField' });
  public readonly $idField = input('id', { alias: 'idField' });
  public readonly $defaultItem = input<T>(undefined, { alias: 'defaultItem' });
  public readonly $valuePrimitive = input(true, { alias: 'valuePrimitive' });

  public clearedEvent = output<void>();
  public selectionChangeEvent = output<T>();

  protected onSelectionChange(event: T) {
    this.selectionChangeEvent.emit(event);
  }

  protected reset() {
    const control = this.$control();
    // 1. Reset to default (sets pristine)
    control.reset();

    if (control.value !== null) {
      // 2. Explicitly set to null and mark dirty if default is not null
      control.setValue(null);
      control.markAsDirty();
    }
    // 3. Ensure dropdown is closed
    this.$dropdownlistReference().toggle(false);
    this.clearedEvent.emit();
  }
}
